/**
 * Secure durable storage for the Tauri Bearer session token(s).
 *
 * ## Why this exists
 * Only the **Tauri WebView** (desktop + mobile) attaches `Authorization:
 * Bearer <token>` (its cross-site `SameSite=Lax` cookie is dropped, so cookies
 * can't be the transport). The plain web build authenticates via the HttpOnly
 * device cookie + `X-Active-Account` selector and NEVER holds a token in JS —
 * so everything here is a no-op on web (guarded by {@link isTauriRuntime}).
 *
 * The token's DURABLE copy lives in a **native secure store** on EVERY Tauri
 * target, via the `tauri-plugin-secure-store` commands
 * (`plugin:secure-store|secure_store_*`):
 *   - **desktop** → the OS keychain (macOS Keychain / Windows Credential Manager
 *     / Linux Secret Service, `keyring` crate);
 *   - **Android** → a hardware-backed `MasterKey` encrypting
 *     `EncryptedSharedPreferences`;
 *   - **iOS** → Keychain Services (`kSecClassGenericPassword`).
 * There is NO plaintext `localStorage` fallback anymore — the pre-plugin mobile
 * build kept the token in plaintext `localStorage`, which this closes. Any
 * pre-existing plaintext token is migrated into the native store on first boot
 * and then wiped (see {@link hydrateSecureTokens}).
 *
 * ## How the synchronous read-path stays synchronous
 * `api.ts`'s `getAuthToken` is called **synchronously** on every request, but
 * native-store access is async. The bridge is a module-level in-memory cache:
 *   - boot (Tauri): {@link hydrateSecureTokens} loads token(s) store → cache
 *     (and one-time migrates any legacy plaintext token into the store). It is
 *     awaited BEFORE the app renders, so the cache is warm before the first
 *     authenticated request.
 *   - write: {@link secureTokenSet}/{@link writeLegacyToken} update the cache
 *     **synchronously** and write through to the native store async.
 *   - read: {@link readCachedToken}/{@link readLegacyToken} return the cache
 *     synchronously.
 * The in-memory cache is an acceptable exposure (XSS can read live memory
 * regardless, exactly as before); the win is that the DURABLE copy leaves
 * plaintext `localStorage`.
 *
 * Each account's token is one store entry keyed by its `userId`; a single legacy
 * slot (no userId) uses {@link LEGACY_TOKEN_ACCOUNT}.
 */

import { ACCOUNTS_STORAGE_KEY, parseAccountsState, type StoredAccount } from "@malla/shared";
import { isTauriRuntime } from "@/lib/platform";

/**
 * Legacy single-token `localStorage` key — the old plaintext durable slot, now
 * used ONLY as the one-time migration source (read once at boot, then wiped).
 * Also doubles as the native-store "account" name for the userId-less legacy
 * token.
 */
export const SESSION_TOKEN_STORAGE_KEY = "malla.session-token";

/** Native-store "account" sentinel for the userId-less legacy session token. */
export const LEGACY_TOKEN_ACCOUNT = SESSION_TOKEN_STORAGE_KEY;

/**
 * Synchronous in-memory token cache (keychain account → token). Sole sync
 * read-source for the API client on Tauri. Empty on web.
 */
const cache = new Map<string, string>();

/**
 * Accounts whose token lives ONLY in the native secure store + the Rust-held
 * active token, and whose value JS has intentionally never cached — the native
 * OAuth deep-link path (Phase 4): Rust stores the token and injects the Bearer,
 * so JS tracks mere PRESENCE (which secure-store slot is authenticated) without
 * ever holding the secret. Kept separate from {@link cache} so the value cache
 * stays "has value" while this stays "has token, value unknown to JS".
 */
const presence = new Set<string>();

/**
 * Secure-store capability, probed once:
 *   - `undefined` → not yet probed
 *   - `true`      → native secure store available (Tauri: desktop keychain,
 *                   Android Keystore, iOS Keychain)
 *   - `false`     → web (no Tauri runtime), or the plugin command errored
 */
let secureStoreAvailable: boolean | undefined;

/** Dynamic import so the web bundle never pulls in the Tauri API at module top. */
async function tauriInvoke<T>(cmd: string, args?: Record<string, unknown>): Promise<T> {
  const { invoke } = await import("@tauri-apps/api/core");
  return invoke<T>(cmd, args);
}

/**
 * Probe (and memoize) whether the native secure store is available. `false` on
 * web (no Tauri runtime); `true` on every Tauri target — the plugin's
 * `secure_store_supported` returns `true` on desktop (OS keychain) AND mobile
 * (Android Keystore / iOS Keychain).
 */
async function probeSecureStore(): Promise<boolean> {
  if (!isTauriRuntime()) {
    return false;
  }
  if (secureStoreAvailable === undefined) {
    try {
      secureStoreAvailable = await tauriInvoke<boolean>(
        "plugin:secure-store|secure_store_supported",
      );
    } catch {
      secureStoreAvailable = false;
    }
  }
  return secureStoreAvailable;
}

// ─── Rust-held active token (Phase 2: "native HttpOnly") ─────────────────────

/**
 * Tell the Rust layer which account's token to inject as `Authorization: Bearer`
 * on API requests (the `api_request` proxy). Rust LOADS the token from the
 * native secure store itself (keyed by `userId`) — the secret is NEVER passed
 * from JS. `null` clears the Rust cache (sign-out).
 *
 * No-op on web (cookie-BFF: the token never lives in JS at all). Awaits the
 * secure-store probe so it's a clean no-op before the store is confirmed.
 */
export async function setActiveNativeToken(userId: string | null): Promise<void> {
  if (!isTauriRuntime() || !(await probeSecureStore())) {
    return;
  }
  try {
    await tauriInvoke("set_active_token", { userId });
  } catch (err) {
    console.warn("[secure-token] set_active_token failed:", err);
  }
}

/**
 * Boot-time prime of the Rust-held active token from the persisted registry's
 * active account (falling back to the legacy slot). Run AFTER
 * {@link hydrateSecureTokens} so the store entry exists, so Rust can inject the
 * Bearer on the very first authenticated request. No-op on web.
 */
export async function primeActiveNativeToken(): Promise<void> {
  if (!isTauriRuntime()) {
    return;
  }
  const raw = readAccountsRaw();
  const state = raw ? parseAccountsState(raw) : { accounts: [], activeUserId: null };
  const activeUserId =
    state.activeUserId ??
    (readCachedToken(LEGACY_TOKEN_ACCOUNT) !== null ? LEGACY_TOKEN_ACCOUNT : null);
  await setActiveNativeToken(activeUserId);
}

// ─── Synchronous cache primitives ────────────────────────────────────────────

/**
 * Synchronous "is the native secure store active?" check, valid AFTER
 * {@link hydrateSecureTokens} has run (awaited before render on Tauri). Used by
 * the registry persistence layer to decide whether to strip tokens out of the
 * `localStorage` JSON. `true` on every Tauri target now (desktop + mobile), so
 * the plaintext token is stripped everywhere; `false` only on web (no token to
 * strip anyway).
 */
export function isSecureStoreReady(): boolean {
  return secureStoreAvailable === true;
}

/** Synchronous read for the API client's `getAuthToken`. */
export function readCachedToken(account: string): string | null {
  return cache.get(account) ?? null;
}

/**
 * Mark a secure-store slot as holding a token whose value Rust owns (the native
 * OAuth deep-link path), WITHOUT caching the secret in JS. Makes
 * {@link hasTokenPresence} true so the transport primes Rust with this slot and
 * the registry records `hasToken` — the value never enters JS.
 */
export function markTokenPresent(account: string): void {
  presence.add(account);
}

/** Clear a presence marker (e.g. after re-keying the legacy slot to a userId). */
export function clearTokenPresence(account: string): void {
  presence.delete(account);
}

/**
 * Whether a token exists for `account` — either its value is cached in JS OR
 * Rust holds it (presence-only). Used to decide the active token slot and the
 * registry `hasToken` marker without requiring the secret in JS.
 */
export function hasTokenPresence(account: string): boolean {
  return cache.has(account) || presence.has(account);
}

/**
 * Re-key the OAuth token from the legacy slot to the account's `userId` slot in
 * the native secure store — done in Rust so the secret never crosses into JS.
 * No-op on web. Call after `get-session` reveals the `userId`.
 */
export async function promoteOauthToken(userId: string): Promise<void> {
  if (!isTauriRuntime()) {
    return;
  }
  try {
    await tauriInvoke("promote_oauth_token", { userId });
  } catch (err) {
    console.warn("[secure-token] promote_oauth_token failed:", err);
  }
}

/** Synchronously update the cache so reads observe the token immediately. */
function setCachedToken(account: string, token: string | null): void {
  if (token) {
    cache.set(account, token);
  } else {
    cache.delete(account);
  }
}

// ─── Per-account token (keyed by userId) ─────────────────────────────────────

/**
 * Durably store a per-account Bearer token: cache (sync) + keychain (async,
 * desktop). On mobile the durable copy stays inline in the registry JSON (this
 * is cache-only there), so the registry must NOT be stripped on mobile.
 */
export async function secureTokenSet(userId: string, token: string): Promise<void> {
  setCachedToken(userId, token);
  if (!(await probeSecureStore())) {
    return;
  }
  try {
    await tauriInvoke("plugin:secure-store|secure_store_set", { userId, token });
    // Store-then-prime (ordered): now that the token is durably in the store,
    // refresh the Rust-held active token so the `api_request` proxy injects THIS
    // account's Bearer. This is the account you just authenticated as, so it is
    // the one that should be active. Account SWITCHES (no store write) are handled
    // by the transport's own re-prime.
    await setActiveNativeToken(userId);
  } catch (err) {
    console.warn("[secure-token] keychain set failed:", err);
  }
}

/** Durably delete a per-account Bearer token: cache (sync) + keychain (async). */
export async function secureTokenDelete(userId: string): Promise<void> {
  setCachedToken(userId, null);
  if (!(await probeSecureStore())) {
    return;
  }
  try {
    await tauriInvoke("plugin:secure-store|secure_store_delete", { userId });
  } catch (err) {
    console.warn("[secure-token] keychain delete failed:", err);
  }
}

async function keychainGet(userId: string): Promise<string | null> {
  try {
    return await tauriInvoke<string | null>("plugin:secure-store|secure_store_get", { userId });
  } catch (err) {
    console.warn("[secure-token] keychain get failed:", err);
    return null;
  }
}

// ─── Legacy single token (userId-less slot) ──────────────────────────────────

/** Reads the OLD plaintext token — migration source only, never a write target. */
function readLegacyLocalStorage(): string | null {
  try {
    return globalThis.localStorage.getItem(SESSION_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** Removes any residual plaintext token from `localStorage` (post-migration). */
function clearLegacyLocalStorage(): void {
  try {
    globalThis.localStorage.removeItem(SESSION_TOKEN_STORAGE_KEY);
  } catch {
    // Storage may be unavailable (private mode, sandbox) — ignore.
  }
}

/**
 * Synchronous read of the legacy session token. Prefers the in-memory cache
 * (warmed at boot from the native store); falls back to the OLD plaintext
 * `localStorage` slot only for the brief pre-hydration window / migration
 * source. Returns `null` on web.
 */
export function readLegacyToken(): string | null {
  const cached = readCachedToken(LEGACY_TOKEN_ACCOUNT);
  if (cached !== null) {
    return cached;
  }
  return readLegacyLocalStorage();
}

/**
 * Durably persist (or clear) the legacy session token — **Tauri only**. Updates
 * the cache synchronously, then writes through to the native secure store
 * (desktop keychain / Android Keystore / iOS Keychain) and purges any plaintext
 * copy so the token never lingers in `localStorage`. There is NO plaintext
 * fallback: on web this is a no-op, and on any Tauri target the store is present.
 */
export function writeLegacyToken(token: string | null): void {
  if (!isTauriRuntime()) {
    return;
  }
  setCachedToken(LEGACY_TOKEN_ACCOUNT, token);
  void persistLegacyToken(token);
}

async function persistLegacyToken(token: string | null): Promise<void> {
  // `writeLegacyToken` already filtered out web; on any Tauri target the native
  // store is available. If the probe somehow fails, we keep only the in-memory
  // cache for this session — we deliberately do NOT write the token to plaintext
  // `localStorage` anymore.
  if (!(await probeSecureStore())) {
    return;
  }
  try {
    if (token) {
      await tauriInvoke("plugin:secure-store|secure_store_set", {
        userId: LEGACY_TOKEN_ACCOUNT,
        token,
      });
    } else {
      await tauriInvoke("plugin:secure-store|secure_store_delete", {
        userId: LEGACY_TOKEN_ACCOUNT,
      });
    }
    // Keep the Rust-held active token in lock-step with the legacy slot: on
    // login prime the legacy account, on sign-out (`token === null`) CLEAR it
    // immediately so the `api_request` proxy stops attaching a stale Bearer.
    await setActiveNativeToken(token ? LEGACY_TOKEN_ACCOUNT : null);
  } catch (err) {
    console.warn("[secure-token] legacy secure-store write failed:", err);
  }
  // Ensure no plaintext copy survives (migration residue).
  clearLegacyLocalStorage();
}

// ─── Boot-time hydration + one-time migration ────────────────────────────────

function readAccountsRaw(): string | null {
  try {
    return globalThis.localStorage.getItem(ACCOUNTS_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeAccountsRaw(value: string): void {
  try {
    globalThis.localStorage.setItem(ACCOUNTS_STORAGE_KEY, value);
  } catch {
    // ignore
  }
}

/**
 * Hydrate the in-memory cache from the native secure store at boot — **all Tauri
 * targets** (desktop + mobile). MUST be awaited before the app renders (see
 * `main.tsx`) so the sync read-path is warm before the first authenticated
 * request.
 *
 * Also performs a ONE-TIME migration of any pre-existing plaintext token (legacy
 * `localStorage` key + per-account `token` in the registry JSON) into the native
 * store, then strips the plaintext copy — this is what closes the old mobile
 * plaintext hole.
 *
 * On web this is a no-op (no token in JS; the `isTauriRuntime()` guard returns
 * early).
 */
export async function hydrateSecureTokens(): Promise<void> {
  if (!isTauriRuntime() || !(await probeSecureStore())) {
    return;
  }
  await hydrateLegacyToken();
  await hydratePerAccountTokens();
}

async function hydrateLegacyToken(): Promise<void> {
  const fromStore = await keychainGet(LEGACY_TOKEN_ACCOUNT);
  if (fromStore) {
    setCachedToken(LEGACY_TOKEN_ACCOUNT, fromStore);
    return;
  }
  // Migrate an old plaintext legacy token, if any, then wipe the plaintext copy.
  const plaintext = readLegacyLocalStorage();
  if (plaintext) {
    setCachedToken(LEGACY_TOKEN_ACCOUNT, plaintext);
    try {
      await tauriInvoke("plugin:secure-store|secure_store_set", {
        userId: LEGACY_TOKEN_ACCOUNT,
        token: plaintext,
      });
      clearLegacyLocalStorage();
    } catch (err) {
      console.warn("[secure-token] legacy migration failed:", err);
    }
  }
}

async function hydratePerAccountTokens(): Promise<void> {
  const raw = readAccountsRaw();
  if (!raw) {
    return;
  }
  const state = parseAccountsState(raw);
  let mutated = false;
  const accounts: StoredAccount[] = [];
  for (const account of state.accounts) {
    if (account.kind === "guest") {
      accounts.push(account);
      continue;
    }
    // Migrate an old plaintext token into the native store, then strip it.
    if (account.token) {
      setCachedToken(account.userId, account.token);
      try {
        await tauriInvoke("plugin:secure-store|secure_store_set", {
          userId: account.userId,
          token: account.token,
        });
        const migrated: StoredAccount = { ...account, hasToken: true };
        delete migrated.token;
        accounts.push(migrated);
        mutated = true;
      } catch (err) {
        console.warn("[secure-token] per-account migration failed:", err);
        accounts.push(account);
      }
      continue;
    }
    // Already migrated: pull the durable token into the cache.
    const token = await keychainGet(account.userId);
    if (token) {
      setCachedToken(account.userId, token);
    }
    accounts.push(account);
  }
  if (mutated) {
    writeAccountsRaw(JSON.stringify({ accounts, activeUserId: state.activeUserId }));
  }
}
